import "./App.scss";
import "./styles/global.scss";
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { Toaster } from "./components/Toaster";
import { useLoading } from "./contexts/LoadingContext";
import { useAuth } from "./contexts/AuthContext";
import { setupAxiosInterceptors } from "./AxiosInstance";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import LoadingBar from "./components/LoadingBar/LoadingBar";
import SidebarLayout from "./layouts/SidebarLayout";
import SignIn from "./pages/SignIn/SignIn";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import SignUp from "./pages/SignUp/SignUp";
import UserOnboarding from "./pages/SignUp/EnhancedSignUp";
import NotFound from "./components/NotFound/NotFound";
import ComingSoon from "./components/ComingSoon/ComingSoon";
import Profile from "./pages/Profile/Profile";
import AssignContainers from "./pages/AssignContainers/AssignContainers";
import Dashboard from "./pages/Dashboard/Dashboard";
import {
  JobManagementRoute,
  TransporterManagementRoute,
  FederationManagementRoute,
} from "./components/RoleBasedRoute/RoleBasedRoute";
import TripHistory from "./pages/TripHistory/TripHistory";
import TripHistoryJob from "./pages/TripHistoryJob/TripHistoryJob";
import initializeAnalytics, { logPageView } from "./utils/analytics";
import initializeClarity from "./utils/clarity";
import ExistingJob from "./components/Jobs/ExistingJob";
import NewJob from "./components/Jobs/NewJob";
import VersionChecker from "./components/VersionChecker/VersionChecker";
import ActiveTrips from "./pages/ActiveTrips/ActiveTrips";
import ViewJob from "./pages/ViewJob/ViewJob";
import LiveTrack from "./pages/LiveTrack/LiveTrack";
import JobHistory from "./pages/Jobs/JobHistory";
import JobHistoryUpload from "./pages/JobHistory/JobHistoryUpload";
import Drivers from "./pages/Drivers/Drivers";
import Transporters from "./pages/Transporters/Transporters";
import Federations from "./pages/Federations";
import Trucks from "./pages/Trucks/Trucks";
import Instructions from "./pages/Instructions/Instructions";
import PermissionExamples from "./components/PermissionExamples/PermissionExamples";
import TestUserManagement from "./components/UserManagement/TestUserManagement";
import Location from "./pages/Location/LocationPage";
import ReportsNew from "./pages/Reports/ReportsNew";
import ReportsMovement from "./pages/Reports/ReportsMovement";
import ReportsRevamp from "./pages/Reports/ReportsRevamp";

const App: React.FC = () => {
  const { isLoading } = useLoading();
  const { logout } = useAuth();

  if (process.env.REACT_APP_ENV === "production") {
    console.log = () => {};
    console.error = () => {}; // Optionally disable other console methods
    console.warn = () => {};
  } else {
    console.warn("Console Logs are enabled");
  }

  useEffect(() => {
    initializeAnalytics();
    logPageView();
    initializeClarity();
    setupAxiosInterceptors(logout);

    // Log page views on route changes
    const unlisten = () => {
      logPageView();
    };
    return () => {
      unlisten();
    };
  }, [logout]); // Ensure the interceptor setup runs when the logout function is available

  return (
    <div className="App">
      <VersionChecker />
      <LoadingBar isLoading={isLoading} />
      <Router>
        <div className="flex">
          <div className="flex-grow p-0">
            <Routes>
              <Route path="/" element={<Navigate to="/jobs/all" />} />

              {/* Public Routes */}
              <Route element={<PublicRoute />}>
                <Route path="/sign-up" element={<SignUp />} />
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/user-onboarding" element={<UserOnboarding />} />
                {/* <Route path="/reset-password" element={<ResetPassword />} /> */}
                <Route
                  path="/test-user-management"
                  element={<TestUserManagement />}
                />
              </Route>

              {/* Private Routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon />} />
                  {/* <Route index element={<Dashboard />} /> // Temporarily disabled Dashboard */}

                </Route>
                <Route path="/jobs" element={<SidebarLayout />}>
                  <Route element={<JobManagementRoute />}>
                    <Route path="" element={<Navigate to="/jobs/all" />} />
                    <Route path="create-new" element={<NewJob />} />
                    <Route path="edit/:jobId/:jobCode" element={<NewJob />} />
                    <Route
                      path="edit"
                      element={<Navigate to="/jobs/all" replace />}
                    />
                    <Route path=":jobStatus" element={<ExistingJob />} />
                    <Route
                      path=":jobStatus/:jobId/:jobCode"
                      element={<ViewJob />}
                    />
                    <Route
                      path=":jobStatus/:jobId/:jobCode/containers"
                      element={<ViewJob />}
                    />
                    <Route
                      path=":jobStatus/:jobId/:jobCode/containers/:tripId"
                      element={<Navigate to="live-tracking" replace />}
                    />
                    <Route
                      path=":jobStatus/:jobId/:jobCode/containers/:tripId/live-tracking"
                      element={<LiveTrack />}
                    />
                    <Route path="history" element={<JobHistory />} />
                    <Route
                      path="history/upload"
                      element={<JobHistoryUpload />}
                    />
                  </Route>
                </Route>
                <Route
                  path="/job/:jobId/:jobCode/assign-containers"
                  element={<SidebarLayout />}
                >
                  <Route index element={<AssignContainers />} />
                </Route>
                <Route path="/transporters" element={<SidebarLayout />}>
                  <Route element={<TransporterManagementRoute />}>
                    <Route index element={<Transporters />} />
                  </Route>
                </Route>
                <Route path="/federations" element={<SidebarLayout />}>
                  <Route element={<FederationManagementRoute />}>
                    <Route index element={<Federations />} />
                  </Route>
                </Route>
                <Route path="/fleet" element={<SidebarLayout />}>
                  <Route
                    index
                    element={<Navigate to="/fleet/drivers" replace />}
                  />
                  <Route path="" element={<Navigate to="/fleet/drivers" />} />
                  <Route path="drivers" element={<Drivers />} />
                  <Route path="trucks" element={<Trucks />} />
                </Route>

                <Route path="/instructions" element={<SidebarLayout />}>
                  <Route index element={<Instructions />} />
                </Route>
                <Route path="/locations" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon/>} />
                  {/* <Route index element={<Location/>} /> */} // Temporarily disabled Location

                </Route>
                <Route path="/trips" element={<SidebarLayout />}>
                  <Route index element={<Navigate to="active" replace />} />
                  <Route path="active" element={<ActiveTrips />} />
                  {/* <Route path="active/:jobId/:jobCode" element={<ViewJob />} />
                  <Route
                    path="active/:jobId/:jobCode/:tripId/live-tracking"
                    element={<LiveTrack />}
                  /> */}
                  <Route path="history" element={<TripHistory />} />
                  {/* <Route path="history/upload" element={<ComingSoon />} /> */}
                  {/* <Route
                    path="history/upload"
                    element={<TripHistoryUpload />}
                  /> */}
                </Route>
                <Route
                  path="/trip-history/job/:jobId"
                  element={<SidebarLayout />}
                >
                  <Route index element={<TripHistoryJob />} />
                </Route>
                <Route path="/reports" element={<SidebarLayout />}>
                  <Route index element={<ReportsRevamp />} />
                  {/* <Route index element={<ReportsMovement />} /> */}

                </Route>
                <Route path="/profile" element={<SidebarLayout />}>
                  <Route index element={<Profile />} />
                </Route>
                <Route path="/settings" element={<SidebarLayout />}>
                  <Route index element={<ComingSoon />} />
                </Route>
                <Route path="/instructions" element={<SidebarLayout />}>
                  <Route index element={<Instructions />} />
                </Route>
                {/* test route */}
                <Route path="/permission-check" element={<SidebarLayout />}>
                  <Route index element={<PermissionExamples />} />
                </Route>
                <Route path="/rbac-test" element={<SidebarLayout />}>
                  <Route index element={<PermissionExamples />} />
                </Route>
              </Route>

              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </div>
      </Router>
      <Toaster></Toaster>
    </div>
  );
};

export default App;
